import { computed, ref, watch, type ComputedRef, type Ref } from "vue";
import type { SearchableMultiselectItem } from "@/components/ui/inputs/SearchableMultiselect.vue";
import type { TItemProps } from "@/types/input";

export function useMultiselectBridge(
  value: Ref,
  optionItems: ComputedRef<any[]>,
  itemProps: ComputedRef<(val: any) => TItemProps>,
  isMultiple: () => boolean | undefined,
  isReturnObject: () => boolean | undefined,
  isInteractionBlocked: ComputedRef<boolean>,
  extendedOpenDialog?: () => void
) {
  const dialogOpen = ref(false);
  const pendingIds = ref<(string | number)[]>([]);

  const modelValue = computed(() => {
    const val = value.value;
    if (isMultiple()) {
      const arr = Array.isArray(val) ? val : [];
      return arr.map((v: Record<string, unknown>) =>
        isReturnObject() ? itemProps.value(v)?.value : v
      );
    }
    if (val == null) return [];
    return [isReturnObject() ? itemProps.value(val)?.value : val];
  });

  watch(dialogOpen, (isOpen) => {
    if (isOpen) pendingIds.value = [...modelValue.value];
  });

  const items = computed<SearchableMultiselectItem[]>(() =>
    optionItems.value.map((item: Record<string, unknown>) => {
      const mapped = itemProps.value(item);
      const raw = item as { color?: string };
      const color = raw?.color ?? (mapped as { color?: string })?.color;
      return {
        id: mapped?.value ?? item,
        name: mapped?.title ?? String(item),
        color: color ? String(color).replace("#", "") : undefined,
        disabled: mapped?.disabled,
      };
    })
  );

  const triggerSelectedItems = computed(
    () =>
      modelValue.value
        .map((id) => items.value.find((it) => it.id === id))
        .filter(Boolean) as SearchableMultiselectItem[]
  );

  function openDialog() {
    if (isInteractionBlocked.value) return;
    dialogOpen.value = true;
    extendedOpenDialog?.();
  }

  function onUpdate(ids: (string | number)[]) {
    if (isMultiple()) {
      if (isReturnObject()) {
        const objects = ids
          .map((id) => optionItems.value.find((it) => itemProps.value(it)?.value === id))
          .filter(Boolean);
        value.value = objects;
      } else {
        value.value = ids;
      }
    } else {
      const singleId = ids[0] ?? null;
      if (isReturnObject() && singleId) {
        value.value =
          optionItems.value.find((it) => itemProps.value(it)?.value === singleId) ?? null;
      } else {
        value.value = singleId;
      }
    }
  }

  /**
   * Close after the current pointer gesture finishes. Closing synchronously on confirm/cancel
   * unmounts the nested dialog under the cursor, so the same click can hit a parent
   * non-persistent dialog scrim and dismiss both layers.
   */
  function closeDialog() {
    requestAnimationFrame(() => {
      dialogOpen.value = false;
    });
  }

  function onConfirm() {
    onUpdate([...pendingIds.value]);
    closeDialog();
  }

  return {
    dialogOpen,
    pendingIds,
    items,
    modelValue,
    triggerSelectedItems,
    openDialog,
    closeDialog,
    onConfirm,
  };
}
